import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpenText, Plus, Pencil, ChefHat, Martini, ImagePlus, UtensilsCrossed } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { menuApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/api/errors";
import type { MenuCategoryDto, MenuItemDto, Station } from "@/lib/api/types";
import { useToast } from "@/hooks/use-toast";
import { cn, formatMoney } from "@/lib/utils";

type SubTab = "items" | "categories";

/** Menu management for one restaurant — every call is scoped by ?tenantId=. */
export function MenuTab({ tenantId }: { tenantId: number }) {
  const [tab, setTab] = useState<SubTab>("items");

  return (
    <div>
      <div className="mb-4 inline-flex rounded-[var(--radius-pill)] border border-border bg-card p-1">
        {(["items", "categories"] as SubTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "rounded-[var(--radius-pill)] px-4 py-1.5 text-sm font-semibold transition-colors",
              tab === t
                ? "bg-gradient-sunset text-primary-foreground shadow-glow"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t === "items" ? "Items" : "Categories"}
          </button>
        ))}
      </div>

      {tab === "categories" ? <CategoriesTab tenantId={tenantId} /> : <ItemsTab tenantId={tenantId} />}
    </div>
  );
}

/* ---------------- categories ---------------- */

function CategoriesTab({ tenantId }: { tenantId: number }) {
  const [editing, setEditing] = useState<MenuCategoryDto | null>(null);
  const [creating, setCreating] = useState(false);

  const categories = useQuery({
    queryKey: ["menu-categories", tenantId],
    queryFn: () => menuApi.categories(tenantId),
  });

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> New category
        </Button>
      </div>
      <Card>
        {categories.isLoading && <Skeleton className="m-5 h-48" />}
        {categories.data && categories.data.length === 0 && (
          <EmptyState
            icon={BookOpenText}
            title="No categories yet"
            description="Create categories like Mains, Drinks, Desserts."
          />
        )}
        {categories.data && categories.data.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Sort order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.data
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell className="font-medium">{cat.name}</TableCell>
                    <TableCell>{cat.sortOrder}</TableCell>
                    <TableCell>
                      <Badge variant={cat.active === false ? "destructive" : "success"}>
                        {cat.active === false ? "Inactive" : "Active"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setEditing(cat)}>
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={creating || !!editing} onOpenChange={(o) => !o && (setCreating(false), setEditing(null))}>
        {(creating || editing) && (
          <CategoryDialog
            tenantId={tenantId}
            category={editing}
            onDone={() => {
              setCreating(false);
              setEditing(null);
            }}
          />
        )}
      </Dialog>
    </>
  );
}

function CategoryDialog({
  tenantId,
  category,
  onDone,
}: {
  tenantId: number;
  category: MenuCategoryDto | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(category?.name ?? "");
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? 0));
  const [active, setActive] = useState(category?.active !== false);

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), sortOrder: Number(sortOrder) || 0, active };
      return category
        ? menuApi.updateCategory(tenantId, category.id, body)
        : menuApi.createCategory(tenantId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menu-categories", tenantId] });
      toast({ title: category ? "Category updated" : "Category created", variant: "success" });
      onDone();
    },
    onError: (err) =>
      toast({ title: "Couldn't save category", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle>{category ? `Edit ${category.name}` : "New category"}</DialogTitle>
      </DialogHeader>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mains" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Sort order</Label>
            <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={active ? "ACTIVE" : "INACTIVE"} onValueChange={(v) => setActive(v === "ACTIVE")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={save.isPending || !name.trim()}>
            {save.isPending ? "Saving…" : "Save category"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

/* ---------------- items ---------------- */

function ItemsTab({ tenantId }: { tenantId: number }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<MenuItemDto | null>(null);
  const [creating, setCreating] = useState(false);

  const items = useQuery({ queryKey: ["menu-items", tenantId], queryFn: () => menuApi.items(tenantId) });
  const categories = useQuery({
    queryKey: ["menu-categories", tenantId],
    queryFn: () => menuApi.categories(tenantId),
  });

  const categoryName = (id: string) => categories.data?.find((c) => c.id === id)?.name ?? "—";

  const toggleAvailable = useMutation({
    mutationFn: (item: MenuItemDto) =>
      menuApi.updateItem(tenantId, item.id, {
        categoryId: item.categoryId,
        name: item.name,
        price: item.price,
        station: item.station,
        available: !item.available,
        sortOrder: item.sortOrder,
      }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["menu-items", tenantId] });
      toast({ title: `${updated.name} is now ${updated.available ? "available" : "unavailable"}`, variant: "success" });
    },
    onError: (err) =>
      toast({ title: "Couldn't update item", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button onClick={() => setCreating(true)} disabled={!categories.data || categories.data.length === 0}>
          <Plus className="h-4 w-4" /> New item
        </Button>
      </div>
      <Card>
        {items.isLoading && <Skeleton className="m-5 h-48" />}
        {items.data && items.data.length === 0 && (
          <EmptyState
            icon={BookOpenText}
            title="No menu items yet"
            description="Add dishes and drinks. Create a category first if you haven't."
          />
        )}
        {items.data && items.data.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead>Station</TableHead>
                <TableHead>Availability</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.data
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                .map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        {item.imageUrl ? (
                          // Cache-bust with a stable-ish key so a re-upload shows without hard refresh.
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="h-10 w-14 shrink-0 rounded-md object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                            <UtensilsCrossed className="h-4 w-4" />
                          </div>
                        )}
                        {item.name}
                      </div>
                    </TableCell>
                    <TableCell>{categoryName(item.categoryId)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{formatMoney(item.price)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="gap-1">
                        {item.station === "BAR" ? <Martini className="h-3 w-3" /> : <ChefHat className="h-3 w-3" />}
                        {item.station}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => toggleAvailable.mutate(item)}
                        disabled={toggleAvailable.isPending}
                        className={cn(
                          "inline-flex items-center rounded-[var(--radius-pill)] px-2.5 py-0.5 text-xs font-semibold transition-colors",
                          item.available
                            ? "bg-success/15 text-success hover:bg-success/25"
                            : "bg-muted text-muted-foreground hover:bg-muted/70",
                        )}
                      >
                        {item.available ? "Available" : "86'd"}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <UploadImageButton tenantId={tenantId} item={item} />
                        <Button variant="outline" size="sm" onClick={() => setEditing(item)}>
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={creating || !!editing} onOpenChange={(o) => !o && (setCreating(false), setEditing(null))}>
        {(creating || editing) && (
          <ItemDialog
            tenantId={tenantId}
            item={editing}
            categories={categories.data ?? []}
            onDone={() => {
              setCreating(false);
              setEditing(null);
            }}
          />
        )}
      </Dialog>
    </>
  );
}

/** Picks an image file and uploads it — the server compresses to ≤640px JPEG on disk. */
function UploadImageButton({ tenantId, item }: { tenantId: number; item: MenuItemDto }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (file: File) => menuApi.uploadItemImage(tenantId, item.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menu-items", tenantId] });
      toast({ title: `Image saved for ${item.name}`, variant: "success" });
    },
    onError: (err) =>
      toast({ title: "Couldn't upload image", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = "";
        }}
      />
      <Button variant="outline" size="sm" disabled={upload.isPending} onClick={() => inputRef.current?.click()}>
        <ImagePlus className="h-3.5 w-3.5" /> {upload.isPending ? "Uploading…" : "Image"}
      </Button>
    </>
  );
}

function ItemDialog({
  tenantId,
  item,
  categories,
  onDone,
}: {
  tenantId: number;
  item: MenuItemDto | null;
  categories: MenuCategoryDto[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(item?.name ?? "");
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? categories[0]?.id ?? "");
  const [price, setPrice] = useState(String(item?.price ?? ""));
  const [station, setStation] = useState<Station>(item?.station ?? "KITCHEN");
  const [available, setAvailable] = useState(item?.available ?? true);
  const [sortOrder, setSortOrder] = useState(String(item?.sortOrder ?? 0));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        categoryId,
        name: name.trim(),
        price: Math.round(Number(price)),
        station,
        available,
        sortOrder: Number(sortOrder) || 0,
      };
      return item ? menuApi.updateItem(tenantId, item.id, body) : menuApi.createItem(tenantId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menu-items", tenantId] });
      toast({ title: item ? "Item updated" : "Item created", variant: "success" });
      onDone();
    },
    onError: (err) =>
      toast({ title: "Couldn't save item", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{item ? `Edit ${item.name}` : "New menu item"}</DialogTitle>
      </DialogHeader>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chicken Curry" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Price (TZS)</Label>
            <Input type="number" min={0} step={1} required value={price} onChange={(e) => setPrice(e.target.value)} placeholder="15000" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Station</Label>
            <Select value={station} onValueChange={(v) => setStation(v as Station)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="KITCHEN">Kitchen</SelectItem>
                <SelectItem value="BAR">Bar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Availability</Label>
            <Select value={available ? "YES" : "NO"} onValueChange={(v) => setAvailable(v === "YES")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="YES">Available</SelectItem>
                <SelectItem value="NO">Unavailable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Sort order</Label>
            <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={save.isPending || !name.trim() || !categoryId || !price}>
            {save.isPending ? "Saving…" : "Save item"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
